import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { CircleOptionsI, TimeUnits } from '@bazis/shared/models/base.types';

const sharedStylesheet = new CSSStyleSheet();
sharedStylesheet.replaceSync(
    `:host{--size-circle:30;--stroke-width:5;--stroke-bg:var(--bazis-border-color);--fill:transparent;--stroke-progress:var(--bazis-color-action);--font-size:inherit;--color:initial;display:block;position:relative;width:calc(var(--size-circle) * 1px);height:calc(var(--size-circle) * 1px)}:host(.bazis-timer_clockwise){svg{transform:rotate(90deg) scaleX(-1)}}svg{transform:rotate(-90deg);width:calc(var(--size-circle) * 1px);height:calc(var(--size-circle) * 1px)}circle{fill:none;cx:calc(var(--size-circle) / 2);cy:calc(var(--size-circle) / 2);r:calc((var(--size-circle) / 2) - var(--stroke-width));stroke-width:var(--stroke-width)}.bazis-timer__background{fill:var(--fill);stroke:var(--stroke-bg)}.bazis-timer__progress{stroke:var(--stroke-progress);stroke-linecap:butt;}.bazis-timer__text{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);font-size:var(--font-size);color:var(--color)}`,
);

@Component({
    selector: 'bazis-timer',
    templateUrl: './timer.component.html',
    // styleUrls: ['timer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false,
})
export class TimerElement implements OnInit, AfterViewInit, OnDestroy {
    public el = inject(ElementRef);

    private cdr = inject(ChangeDetectorRef);

    readonly disabled = input<boolean>();

    readonly type = input<'clock' | 'circle' | 'default'>('default');

    readonly circleOptions = input<CircleOptionsI>({ radius: 25 });

    readonly isClockwise = input<boolean>(true);

    readonly startValue = input<number>(10);

    // входящее значение в еденицах измерения
    readonly unitsIn = input<TimeUnits>(TimeUnits.ss);

    // вывод таймера в еденицах измерения
    readonly unitsOut = input<TimeUnits>(TimeUnits.ss);

    // что надо сделать после окончания таймера
    afterStopTimer = output();

    strokeDashOffset = 0;

    timeOutputRemaining;

    timeLeft: number;

    private timerId: any;

    lengthCircle: number;

    @ViewChild('circleRef') circleRef: ElementRef;

    @HostBinding('class') get setStyle() {
        return {
            'bazis-timer': true,
            'bazis-timer--disabled': this.disabled(),
            [`bazis-timer_${this.type()}`]: !!this.type(),
            'bazis-timer_clockwise': this.isClockwise(),
        };
    }

    ngOnInit(): void {
        const host = this.el.nativeElement;
        if (host && host.shadowRoot) {
            const shadowRoot = host.shadowRoot;
            const styles = shadowRoot.querySelectorAll('style');
            styles.forEach((style) => style.remove());
            shadowRoot.adoptedStyleSheets = [sharedStylesheet];
        }

        this.timeOutputRemaining = this.formatValue(this.unitsOut());
        this.timeLeft = this.timeOutputRemaining[this.unitsOut()];
    }

    // нам необходимо получить точную длину окружности, после инициализации svg геометрии
    // это предотвратит запаздывание анимации относительно начала таймера
    ngAfterViewInit() {
        if (this.type() === 'circle') {
            const circleElement = this.circleRef.nativeElement as SVGGeometryElement;
            this.lengthCircle = circleElement.getTotalLength();
            this.startTimerTypeCircle();
        } else {
            this.startTimerTypeClock();
        }
    }

    ngOnDestroy(): void {
        this.stopTimer();
    }

    startTimerTypeCircle(): void {
        this.stopTimer();

        const totalDurationMs = this.timeOutputRemaining.ms;

        // ШАГ 1: Начальное состояние (синхронно)
        // Показываем начальное значение таймера и идеально полный круг
        this.timeLeft = this.formatValue('ms', totalDurationMs)[this.unitsOut()];
        this.cdr.detectChanges();

        // ШАГ 2: Фиксируем время ПОСЛЕ того, как отрисовали первый кадр
        const startTime = performance.now();

        const tick = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const actualTimeLeftMs = totalDurationMs - elapsed;

            if (actualTimeLeftMs > 0) {
                // ЛОГИКА ЦИФР
                // Передаем в formatValue значение, округленное ВВЕРХ до целой секунды.
                // При 4999мс передаст 5000мс -> formatValue (Math.trunc) вернет 5.
                const displayValueMs = Math.ceil(actualTimeLeftMs / 1000) * 1000;
                this.timeLeft = this.formatValue('ms', displayValueMs)[this.unitsOut()];

                // ЛОГИКА КРУГА
                // Используем ЧИСТОЕ время. Сразу после старта (например, на 4950мс)
                // progress станет 0.99, и offset станет > 0 (круг начнет уменьшаться).
                const progress = actualTimeLeftMs / totalDurationMs;
                this.strokeDashOffset = this.lengthCircle * (1 - progress);

                this.timerId = requestAnimationFrame(tick);
            } else {
                this.completeTimer();
            }
            this.cdr.detectChanges();
        };

        this.timerId = requestAnimationFrame(tick);
    }

    startTimerTypeClock() {
        // TODO: ввод таймера в часовом табло
        // const targetDate = new Date('Dec 31, 2025 23:59:59').getTime();
        // setInterval(() => {
        //     const now = new Date().getTime();
        //     const diff = targetDate - now;
        //     const d = Math.floor(diff / (1000 * 60 * 60 * 24));
        //     const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        //     const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        //     const s = Math.floor((diff % (1000 * 60)) / 1000);
        //     document.getElementById('days').innerText = d;
        //     document.getElementById('hours').innerText = h;
        //     document.getElementById('minutes').innerText = m;
        //     document.getElementById('seconds').innerText = s;
        // }, 1000);
        // const daysEl = document.getElementById("days");
        // if (daysEl) {
        //   daysEl.innerText = d;
        // }
        // TODO: вывод дефолтного таймера
    }

    private completeTimer(): void {
        this.stopTimer();
        this.afterStopTimer.emit();
        this.timeLeft = 0;
        this.strokeDashOffset = this.lengthCircle;
    }

    stopTimer(): void {
        if (this.timerId) {
            cancelAnimationFrame(this.timerId); // Останавливаем rAF
            this.timerId = null;
        }
    }

    formatValue(units, value = this.startValue()): any {
        // преобразование startValue по units
        let formattedValueMs: number;
        let formattedValueS: number;
        let formattedValueM: number;
        let formattedValueH: number;
        switch (units) {
            case TimeUnits.hh:
                formattedValueMs = value * 1000 * 60 * 60;
                formattedValueS = value * 60 * 60;
                formattedValueM = value * 60;
                formattedValueH = value;
                break;
            case TimeUnits.mm:
                formattedValueMs = value * 1000 * 60;
                formattedValueS = value * 60;
                formattedValueM = value;
                formattedValueH = Math.trunc(value / 60);
                break;
            case TimeUnits.ss:
                formattedValueMs = value * 1000;
                formattedValueS = value;
                formattedValueM = Math.trunc(value / 60);
                formattedValueH = Math.trunc(value / 3600);
                break;
            // ms, timestamp
            default:
                formattedValueMs = value;
                formattedValueS = Math.trunc(value / 1000);
                formattedValueM = Math.trunc(value / (1000 * 60));
                formattedValueH = Math.trunc(value / (1000 * 3600));
                break;
        }
        return {
            ms: formattedValueMs,
            ss: formattedValueS,
            mm: formattedValueM,
            hh: formattedValueH,
        };
    }
}
